package one.healthium.uscreen.navigation

import androidx.lifecycle.ViewModelStore
import com.arkivanov.decompose.Child
import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.router.children.ChildNavState
import com.arkivanov.decompose.router.children.ChildNavState.Status
import com.arkivanov.decompose.router.children.NavState
import com.arkivanov.decompose.router.children.SimpleChildNavState
import com.arkivanov.decompose.router.children.SimpleNavigation
import com.arkivanov.decompose.router.children.children
import com.arkivanov.decompose.value.Value
import kotlinx.serialization.Serializable
import one.healthium.base.preferences.AppPreferences
import one.healthium.decompose.viewModelStoreOwner
import one.healthium.uscreen.domain.models.UScreenFlowModel
import one.healthium.uscreen.ui.components.UScreenProgressState
import one.primehealth.app.TargetPlatform
import one.primehealth.app.targetPlatform

class DefaultUScreenComponent(
    componentContext: ComponentContext,
    override val model: UScreenFlowModel,
    private val appPreferences: AppPreferences,
    private val onFinished: () -> Unit,
    private val onSignInClicked: () -> Unit = {},
    private val onRestoreClicked: () -> Unit = {},
    private val onPurchaseClicked: () -> Unit = {},
    private val onOpenUrlClicked: (String) -> Unit = {},
) : UScreenComponent, ComponentContext by componentContext {

    private val allScreens = model.groups.flatMap { group ->
        group.screens.filter { screen ->
            screen.platform == null || screen.platform == currentPlatform
        }
    }

    companion object {
        private val currentPlatform: String = when (targetPlatform()) {
            TargetPlatform.Android -> "android"
            TargetPlatform.iOS -> "ios"
            else -> "web"
        }
    }

    @Serializable
    data class ScreenConfig(val screenIndex: Int)

    @Serializable
    data class ScreensNavState(
        val activeIndex: Int,
        val previousIndex: Int? = null,
        val configs: List<ScreenConfig>,
    ) : NavState<ScreenConfig> {
        override val children: List<ChildNavState<ScreenConfig>>
            get() = configs.mapIndexed { i, config ->
                val status = when {
                    i == activeIndex -> Status.RESUMED
                    i == previousIndex -> Status.STARTED
                    i == activeIndex + 1 -> Status.CREATED
                    else -> Status.DESTROYED
                }
                SimpleChildNavState(config, status)
            }
    }

    sealed interface NavEvent {
        data object Next : NavEvent
        data object Back : NavEvent
        data object ClearPrevious : NavEvent
        data class GoTo(val index: Int) : NavEvent
    }

    private val nav = SimpleNavigation<NavEvent>()

    override val screenChildren: Value<UScreenComponent.ScreenChildState> =
        children<ComponentContext, ScreenConfig, UScreenComponent.UScreenChild, NavEvent, ScreensNavState, UScreenComponent.ScreenChildState>(
            source = nav,
            stateSerializer = ScreensNavState.serializer(),
            initialState = {
                val configs = allScreens.indices.map { ScreenConfig(it) }
                ScreensNavState(activeIndex = 0, configs = configs)
            },
            key = "uscreen",
            navTransformer = { state: ScreensNavState, event: NavEvent ->
                when (event) {
                    NavEvent.Next -> {
                        val next = state.activeIndex + 1
                        if (next < allScreens.size) {
                            state.copy(activeIndex = next, previousIndex = state.activeIndex)
                        } else {
                            state
                        }
                    }

                    NavEvent.Back -> {
                        val prev = state.activeIndex - 1
                        if (prev >= 0) {
                            state.copy(activeIndex = prev, previousIndex = state.activeIndex)
                        } else {
                            state
                        }
                    }

                    NavEvent.ClearPrevious -> {
                        state.copy(previousIndex = null)
                    }

                    is NavEvent.GoTo -> {
                        if (event.index in allScreens.indices && event.index != state.activeIndex) {
                            state.copy(activeIndex = event.index, previousIndex = state.activeIndex)
                        } else {
                            state
                        }
                    }
                }
            },
            stateMapper = { state: ScreensNavState, children: List<Child<ScreenConfig, UScreenComponent.UScreenChild>> ->
                UScreenComponent.ScreenChildState(
                    items = children,
                    activeIndex = state.activeIndex,
                    previousIndex = state.previousIndex,
                )
            },
            onEventComplete = { _, _, _ -> },
            backTransformer = { state: ScreensNavState ->
                if (state.activeIndex > 0) {
                    { state.copy(activeIndex = state.activeIndex - 1, previousIndex = state.activeIndex) }
                } else {
                    null
                }
            },
            childFactory = { config: ScreenConfig, _: ComponentContext ->
                UScreenComponent.UScreenChild(
                    screen = allScreens[config.screenIndex],
                    screenIndex = config.screenIndex,
                    progressState = computeProgress(config.screenIndex),
                )
            },
        )

    override val viewModelStore: ViewModelStore = viewModelStoreOwner().viewModelStore

    override fun navigateNext() {
        val currentIndex = screenChildren.value.activeIndex
        if (currentIndex + 1 >= allScreens.size) {
            appPreferences.onboardingPassed = true
            onFinished()
        } else {
            nav.navigate(NavEvent.Next)
        }
    }

    override fun onBackClicked() {
        nav.navigate(NavEvent.Back)
    }

    override fun onSignIn() {
        onSignInClicked()
    }

    override fun onRestore() {
        onRestoreClicked()
    }

    override fun onPurchase() {
        onPurchaseClicked()
    }

    override fun onOpenUrl(url: String) {
        onOpenUrlClicked(url)
    }

    override fun onGoToScreen(screenId: String) {
        val index = allScreens.indexOfFirst { it.id == screenId }
        if (index >= 0) {
            nav.navigate(NavEvent.GoTo(index))
        }
    }

    private fun computeProgress(screenIndex: Int): UScreenProgressState {
        var screensBeforeGroup = 0
        for ((groupIndex, group) in model.groups.withIndex()) {
            val screensInGroup = group.screens.size
            if (screenIndex < screensBeforeGroup + screensInGroup) {
                return UScreenProgressState(
                    groups = model.groups,
                    currentGroupIndex = groupIndex,
                    screensCompletedInGroup = screenIndex - screensBeforeGroup,
                    totalScreensInGroup = screensInGroup,
                )
            }
            screensBeforeGroup += screensInGroup
        }
        val lastGroup = model.groups.lastIndex
        return UScreenProgressState(
            groups = model.groups,
            currentGroupIndex = lastGroup,
            screensCompletedInGroup = model.groups.last().screens.size,
            totalScreensInGroup = model.groups.last().screens.size,
        )
    }
}
